import {
  FunctionComponent,
  PropsWithChildren,
  memo,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import { isMobileOnly } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ConfigContext, DetailsModalContext } from '../../../../context';
import {
  StoryCard,
  StoryCardContentGallery,
  StoryCardQuoteContent,
  StoryCardType,
} from '../../../../services';
import {
  CardActions,
  CardOpenButton,
  IconLabel,
  getCssVar,
  getStoryCardName,
  getStoryCardPreviewUrl,
  useCardBookmarks,
  useCardDate,
} from '../../../../shared';

import classNames from 'classnames';
import classes from './HorizontalStoryFeedCardWrapper.module.scss';

interface HorizontalStoryFeedCardWrapperProps {
  card: StoryCard;
}

export const HorizontalStoryFeedCardWrapper: FunctionComponent<
  PropsWithChildren<HorizontalStoryFeedCardWrapperProps>
> = memo(({ card, children }) => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const { setDetailsModalData } = useContext(DetailsModalContext);

  const { showPostedTime = true } = useContext(ConfigContext).config.elements.card;

  const {
    id: cardId,
    storyId,
    type,
    dateToDisplay,
    content,
    externalTags,
    externalStatusTags,
  } = card;

  const { cardBookmarks } = useCardBookmarks({
    storyCard: card,
    iconSize: 18,
    className: classes['card__bookmarks'],
  });

  const date = useCardDate(dateToDisplay);

  const {
    styles: { backgroundColor },
  } = content;

  const { gallery } = content as StoryCardContentGallery;

  const { quoteSource } = content as StoryCardQuoteContent;

  const previewUrl = getStoryCardPreviewUrl(card);

  const isImageGallery = useMemo(
    () =>
      [StoryCardType.IMAGE, StoryCardType.QUOTE].includes(type) && gallery && gallery.length > 1,
    [gallery, type]
  );

  const shouldRenderOverlay = useMemo(
    () =>
      isImageGallery ||
      (previewUrl &&
        [
          StoryCardType.VIDEO,
          StoryCardType.AUDIO,
          StoryCardType.PDF,
          StoryCardType.POLL,
          StoryCardType.EVENT,
        ].includes(type)),
    [isImageGallery, previewUrl, type]
  );

  const cardClickHandler = useCallback(
    (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      if ((event.target as HTMLElement).tagName.toLowerCase() === 'a') {
        event.preventDefault();
      }

      isMobileOnly
        ? navigate(`/details/${storyId}/${cardId}`)
        : setDetailsModalData({ storyId, cardId });
    },
    [cardId, navigate, setDetailsModalData, storyId]
  );

  const hasTagsIcon = useMemo(
    () => externalTags.length > 0 || externalStatusTags.length > 0,
    [externalTags, externalStatusTags]
  );

  const tagsClickHandler = useCallback(
    (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      event.stopPropagation();

      isMobileOnly
        ? navigate(`/details/${storyId}/${cardId}`)
        : setDetailsModalData({ storyId, cardId });
    },
    [cardId, navigate, setDetailsModalData, storyId]
  );

  const iconId = useMemo(() => {
    switch (type) {
      case StoryCardType.ARTICLE:
      case StoryCardType.POST:
        return 'article';
      case StoryCardType.IMAGE:
        return isImageGallery ? 'gallery' : type.toLowerCase();
      case StoryCardType.QUOTE:
        return isImageGallery ? 'gallery' : quoteSource?.toLowerCase();
      default:
        return type.toLowerCase();
    }
  }, [isImageGallery, quoteSource, type]);

  const iconColor = useMemo(
    () => (!previewUrl ? getCssVar('--card-overlay-icon-color') : '#ffffff'),
    [previewUrl]
  );

  return (
    <>
      <div
        className={classNames(`card-${cardId}`, classes['card'])}
        style={{ backgroundColor }}
        onClick={cardClickHandler}
      >
        <CardOpenButton label={getStoryCardName({ card, t })} />

        <div
          className={classNames(classes['card__teaser'], {
            [classes['card__teaser--placeholder']]: !previewUrl,
            [classes['card__teaser--overlay']]: shouldRenderOverlay,
          })}
          {...(previewUrl && { style: { backgroundImage: `url(${previewUrl})` } })}
        >
          {(!previewUrl || shouldRenderOverlay) && (
            <IconLabel
              className={classes['card__teaser-icon']}
              iconId={iconId}
              iconSize={32}
              color={iconColor}
              singleColor
            />
          )}
        </div>

        <div className={classes['card__content']}>
          {children}
          {showPostedTime && date && <div className={classes['card__posted-time']}>{date}</div>}
        </div>
      </div>

      {hasTagsIcon && (
        <IconLabel
          className={classes['card__tags']}
          iconSize={18}
          iconId={'tags'}
          ariaLabel={t('cardActions.show-tags')}
          color={getCssVar('--action-pane-icon-color')}
          hoverColor={getCssVar('--action-pane-icon-hover-color')}
          onClick={tagsClickHandler}
        />
      )}

      {cardBookmarks}

      <CardActions card={card} isHorizontalFeedCard />
    </>
  );
});
